import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ServiceCard from '../services/pipeline/ServiceCard';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS, resolveServiceKeyValForOrderType } from '../../helpers/constants';
import AddEditServiceSidebar from '../services/pipeline/AddEditServiceSidebar';
import AddEditReportSideBar from '../services/pipeline/lien-search/AddEditReportSideBar';
import AlertModal from '../AlertModal';
import { getFullAddress } from '../../helpers/global';
import LoadingServiceCard from '../services/pipeline/LoadingServiceCard';
import AddEditEstoppelReportSidebar from '../services/pipeline/estoppel/AddEditEstoppelReportSidebar';
import AddEditSurveyReportSidebar from '../services/pipeline/survey/AddEditSurveyReportSidebar';
import { useHistory, useLocation } from 'react-router-dom';
import { ORDER_TYPES } from '../../helpers/constants';

const ServiceSlidingBar = ({
  service: initialService,
  orderType,
  onClose,
  refreshOrders,
  orderId,
  serviceId,
  reportType,
  rowOrderId
}) => {
  const [service, setService] = useState();
  const [reportSidebarMeta, setReportSidebarMeta] = useState(null);
  const [addUpdateServiceMeta, setAddUpdateServiceMeta] = useState(null);
  const [deleteModalMeta, setDeleteModalMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showServiceCard, setShowServiceCard] = useState(false);
  const isClosedRef = useRef(false);
  const isServiceCardDeepLink = Boolean(serviceId && orderType && !orderId && !reportType);
  const isReportDeepLink = Boolean(orderId && serviceId);

  const [isVisible, setIsVisible] = useState(
    Boolean(initialService?._id) || isServiceCardDeepLink || isReportDeepLink
  );
  const history = useHistory();
  const location = useLocation();
  const serviceKeyValWithOrderType = useMemo(
    () => resolveServiceKeyValForOrderType(orderType),
    [orderType]
  );

  const handleServiceUpdate = useCallback(
    async updatedService => {
      if (updatedService?._id) {
        setService(updatedService);
      } else {
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.FETCH_SERVICES,
          method: 'POST',
          requestBody: {
            filter: { serviceFilters: { _id: initialService?._id || serviceId } }
          }
        });
        if (!error && response?.[0]) {
          setService(response[0]);
        }
      }
      refreshOrders?.();
    },
    [refreshOrders, initialService?._id, serviceId]
  );

  const handleServiceDelete = () => {
    setDeleteModalMeta({ toBeDeletedService: service });
  };

  const handleServiceAddEdit = () => {
    setAddUpdateServiceMeta({ ...service });
  };

  const handleShowFilterSidebarChange = useCallback(show => {}, []);

  const handleServiceSortOptionChange = useCallback(option => {}, []);

  const handleOrderTabChange = useCallback(
    nextOrderType => {
      if (reportType) return;

      const searchParams = new URLSearchParams(window.location.search);
      const currentOrderType = searchParams.get('orderType');

      if (nextOrderType) {
        if (currentOrderType === nextOrderType) return;
        searchParams.set('orderType', nextOrderType);
      } else {
        if (!currentOrderType) return;
        searchParams.delete('orderType');
      }

      const newSearch = searchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash || ''}`;
      window.history.replaceState(window.history.state, '', newUrl);
    },
    [reportType]
  );

  const deleteService = async () => {
    const { toBeDeletedService } = deleteModalMeta;
    setDeleteModalMeta({ ...deleteModalMeta, deletingService: true });
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_WITH_ID(toBeDeletedService._id),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error);
        return;
      }
      setDeleteModalMeta(null);
      toast.success('Service deleted successfully');
      refreshOrders?.();
    } catch (e) {
      toast.error(e);
    } finally {
      setDeleteModalMeta(null);
    }
  };

  useEffect(() => {
    const isEstoppel = reportType === ORDER_TYPES.ESTOPPEL;
    const isSurvey = reportType === ORDER_TYPES.SURVEY;
    const reportSidebarType = isSurvey
      ? ORDER_TYPES.SURVEY
      : isEstoppel
        ? ORDER_TYPES.ESTOPPEL
        : ORDER_TYPES.LIEN_SEARCH;

    const fetchReportSideBarMetaData = async () => {
      setShowServiceCard(false);
      setReportSidebarMeta({ show: true, loading: true, type: reportSidebarType });
      const reportEndpoint = isSurvey
        ? ENDPOINTS.SURVEY_REPORT_BASE
        : isEstoppel
          ? ENDPOINTS.ESTOPPEL_REPORT_BASE
          : ENDPOINTS.REPORT_BASE;

      const [servicesResult, reportResult] = await Promise.all([
        makeRESTApiRequest({
          endpoint: ENDPOINTS.FETCH_SERVICES,
          requestBody: {
            filter: { serviceFilters: { _id: serviceId } }
          }
        }),
        makeRESTApiRequest({
          endpoint: reportEndpoint,
          method: 'POST',
          requestBody: { orderId }
        })
      ]);

      const { response: servicesResponse, error: servicesError } = servicesResult;
      const { response: reportResponse, error: reportError } = reportResult;

      // Handle errors if any
      if (servicesError || reportError) {
        console.error('API Error:', { servicesError, reportError });
        setReportSidebarMeta({ show: false, loading: false, type: reportSidebarType });
        return;
      }

      setService(servicesResponse[0]);

      const baseReportMeta = {
        show: true,
        service: servicesResponse[0],
        loading: false,
        orderReport: {
          ...servicesResponse,
          report: {
            ...reportResponse,
            propertyAddress: getFullAddress(Array.isArray(servicesResponse) ? servicesResponse[0] : servicesResponse),
            owner: servicesResponse?.sellerName || servicesResponse?.[0]?.sellerName
          },
          reportId: reportResponse._id
        }
      };

      if (isSurvey) {
        setReportSidebarMeta({
          show: true,
          service: servicesResponse[0],
          loading: false,
          type: ORDER_TYPES.SURVEY,
          orderReport: {
            ...servicesResponse[0],
            report: reportResponse,
            reportId: reportResponse._id
          }
        });
      } else if (isEstoppel) {
        setReportSidebarMeta({
          ...baseReportMeta,
          type: ORDER_TYPES.ESTOPPEL
        });
      } else {
        setReportSidebarMeta({
          ...baseReportMeta,
          type: ORDER_TYPES.LIEN_SEARCH
        });
      }
      setIsVisible(true);
    };

    if (isReportDeepLink && !initialService?._id && !isClosedRef.current) {
      fetchReportSideBarMetaData();
    }
  }, [orderId, serviceId, reportType, initialService?._id]);

  const fetchService = async () => {
    setIsLoading(true);
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_SERVICES,
      method: 'POST',
      requestBody: {
        filter: { serviceFilters: { _id: initialService?._id || serviceId } }
      }
    });
    if (error) {
      toast.error(error);
      setIsLoading(false);
      return;
    }
    setService(response[0]);
    setIsLoading(false);
  };

  const clearServiceUrlParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const hadServiceParams =
      searchParams.has('orderId') ||
      searchParams.has('serviceId') ||
      searchParams.has('orderType') ||
      searchParams.has('reportType');

    if (!hadServiceParams) return;

    searchParams.delete('orderId');
    searchParams.delete('serviceId');
    searchParams.delete('orderType');
    searchParams.delete('reportType');
    const newSearch = searchParams.toString();

    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : ''
    });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    if (initialService?._id) {
      isClosedRef.current = false;
      fetchService();
      setShowServiceCard(true);
      setIsVisible(true);
      return;
    }

    if (isClosedRef.current) return;

    if (serviceId && orderType && !orderId && !reportType) {
      setShowServiceCard(true);
      setIsVisible(true);
      fetchService();
    }
    // orderType intentionally omitted — tab switches sync URL via replaceState only
  }, [initialService?._id, serviceId, orderId, reportType]);

  const handleClose = () => {
    isClosedRef.current = true;
    clearServiceUrlParams();
    setIsVisible(false);
    setShowServiceCard(false);
    setReportSidebarMeta(null);
    onClose?.();
  };

  return (
    <SlidingSideBar
      style={{ width: '100%', left: 'unset', right: 0 }}
      visible={isVisible}
      onClose={handleClose}
      title={
        <>
          <span>Service Details</span>
        </>
      }
    >
      {isLoading ? (
        <LoadingServiceCard />
      ) : (
        service &&
        showServiceCard && (
          <>
            <ServiceCard
              key={`service-card-${service?._id}`}
              service={service}
              onServiceUpdate={handleServiceUpdate}
              onServiceDeleteClick={handleServiceDelete}
              onServiceEditClick={handleServiceAddEdit}
              onShowFilterSidebarChange={handleShowFilterSidebarChange}
              onServiceSortOptionChange={handleServiceSortOptionChange}
              onOrderTabChange={handleOrderTabChange}
              serviceKeyValWithOrderType={serviceKeyValWithOrderType}
            />
            <AlertModal
              show={deleteModalMeta !== null}
              onHide={() => setDeleteModalMeta(null)}
              onDismissClick={() => setDeleteModalMeta(null)}
              onContinueClick={deleteService}
              showProgress={deleteModalMeta?.deletingService}
              alertText={`Are you sure you want to delete this service "${deleteModalMeta?.toBeDeletedService
                .fileName || ''}"? This action cannot be undone!`}
              progressText="Deleting service..."
            />
            <AddEditServiceSidebar
              key={`edit-service-sidebar-${service?._id}`}
              onServiceAddUpdate={handleServiceUpdate}
              show={addUpdateServiceMeta !== null}
              editingService={addUpdateServiceMeta}
              onHide={() => setAddUpdateServiceMeta(null)}
            />
          </>
        )
      )}
      {reportSidebarMeta?.show && reportSidebarMeta?.type === ORDER_TYPES.SURVEY && (
        <AddEditSurveyReportSidebar
          loading={reportSidebarMeta?.loading}
          show={reportSidebarMeta?.show}
          survey={reportSidebarMeta?.orderReport}
          service={reportSidebarMeta?.service}
          surveyOrderId={orderId}
          onAfterSave={handleServiceUpdate}
          onHide={() => {
            setReportSidebarMeta(null);
            if (service) setShowServiceCard(true);
          }}
        />
      )}
      {reportSidebarMeta?.show && reportSidebarMeta?.type === ORDER_TYPES.ESTOPPEL && (
        <AddEditEstoppelReportSidebar
          loading={reportSidebarMeta?.loading}
          show={reportSidebarMeta?.show}
          estoppel={reportSidebarMeta?.orderReport}
          service={reportSidebarMeta?.service}
          estoppelOrderId={orderId}
          onAfterSave={handleServiceUpdate}
          onOrderHoaUpdated={() => handleServiceUpdate()}
          onHide={() => {
            setReportSidebarMeta(null);
            if (service) setShowServiceCard(true);
          }}
        />
      )}
      {reportSidebarMeta?.show && reportSidebarMeta?.type === ORDER_TYPES.LIEN_SEARCH && (
        <AddEditReportSideBar
          loading={reportSidebarMeta?.loading}
          show={reportSidebarMeta?.show}
          lienSearch={reportSidebarMeta?.orderReport}
          onAfterSave={handleServiceUpdate}
          onHide={() => {
            setReportSidebarMeta(null);
            if (service) setShowServiceCard(true);
          }}
          service={reportSidebarMeta?.service}
          orderType={ORDER_TYPES.LIEN_SEARCH}
        />
      )}
    </SlidingSideBar>
  );
};

export default ServiceSlidingBar;
